import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const fmtHM = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Pacific/Noumea", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const fmtDur = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}` : `${m} min`);

// Palette alignée sur la carte (HistoryMap.tripColor)
const PALETTE = ["#17A2B8", "#F5821F", "#10B981", "#A855F7", "#EF4444", "#3B82F6", "#EAB308", "#EC4899", "#14B8A6", "#F97316"];

const mercator = (lat, lon) => {
  const x = (lon + 180) / 360;
  const r = (lat * Math.PI) / 180;
  const y = (1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2;
  return { x, y };
};

// Dessine tous les trajets sur un canvas (fond sombre, tracés colorés + départ/arrivée) → dataURL PNG
function tripsImage(trips, w = 1400, h = 800) {
  const proj = [];
  trips.forEach((t) => (t.points || []).forEach((p) => proj.push(mercator(p.lat, p.lon))));
  if (proj.length < 2) return null;
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  proj.forEach((p) => { minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y); });
  const pad = 40;
  const spanX = (maxX - minX) || 1e-9, spanY = (maxY - minY) || 1e-9;
  const scale = Math.min((w - 2 * pad) / spanX, (h - 2 * pad) / spanY);
  const offX = (w - spanX * scale) / 2, offY = (h - spanY * scale) / 2;
  const px = (lat, lon) => { const m = mercator(lat, lon); return [offX + (m.x - minX) * scale, offY + (m.y - minY) * scale]; };
  const cv = document.createElement("canvas"); cv.width = w; cv.height = h;
  const ctx = cv.getContext("2d");
  ctx.fillStyle = "#0b0d12"; ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = "rgba(255,255,255,0.05)"; ctx.lineWidth = 1;
  for (let gx = 0; gx <= w; gx += 70) { ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, h); ctx.stroke(); }
  for (let gy = 0; gy <= h; gy += 70) { ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(w, gy); ctx.stroke(); }
  trips.forEach((t, i) => {
    const pts = t.points || [];
    if (pts.length < 2) return;
    const col = PALETTE[i % PALETTE.length];
    ctx.strokeStyle = col; ctx.lineWidth = 2.5; ctx.lineJoin = "round"; ctx.beginPath();
    pts.forEach((p, j) => { const [X, Y] = px(p.lat, p.lon); if (j === 0) ctx.moveTo(X, Y); else ctx.lineTo(X, Y); });
    ctx.stroke();
    const [sx, sy] = px(pts[0].lat, pts[0].lon);
    const [ex, ey] = px(pts[pts.length - 1].lat, pts[pts.length - 1].lon);
    ctx.fillStyle = "#10B981"; ctx.beginPath(); ctx.arc(sx, sy, 4, 0, 7); ctx.fill();
    ctx.fillStyle = "#EF4444"; ctx.beginPath(); ctx.arc(ex, ey, 4, 0, 7); ctx.fill();
  });
  return cv.toDataURL("image/png");
}


const dl = (blob, name) => {
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(u), 1500);
};

const addr = (a, i, key, t) => {
  if (a[i] && a[i][key]) return a[i][key];
  const p = key === "start" ? t.points?.[0] : t.points?.[t.points.length - 1];
  return p ? `${p.lat.toFixed(5)}, ${p.lon.toFixed(5)}` : "";
};

const ROWS = (trips, a) => trips.map((t, i) => [
  t.vehicle_name || "", t.plate || "",
  fmtHM(t.start), fmtHM(t.end),
  addr(a, i, "start", t), addr(a, i, "end", t),
  String(t.distance_km ?? ""), fmtDur(t.duration_min || 0), String(t.max_speed ?? ""),
  t.driver_name || "",
]);

const HEAD = ["Véhicule", "Plaque", "Départ", "Arrivée", "Adresse départ", "Adresse arrivée", "Km", "Durée", "V.max", "Conducteur"];

export function exportTripsCSV(trips, a = {}, label = "") {
  const esc = (s) => `"${String(s).replace(/"/g, '""')}"`;
  const lines = [HEAD.map(esc).join(";"), ...ROWS(trips, a).map((r) => r.map(esc).join(";"))];
  dl(new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" }), `historique_${label || "flotte"}.csv`);
}

export function exportTripsPDF(trips, a = {}, label = "") {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  doc.setFontSize(14); doc.setTextColor(23, 162, 184);
  doc.text("Historique de flotte — Boomerang Tracking", 14, 14);
  doc.setFontSize(9); doc.setTextColor(80);
  doc.text(`${label} · ${trips.length} trajet(s) · ${trips.reduce((s, t) => s + (t.distance_km || 0), 0).toFixed(1)} km`, 14, 20);

  // Visuel des trajets (carte dessinée) en tête du PDF
  let tableY = 25;
  try {
    const img = tripsImage(trips);
    if (img) {
      const iw = 180, ih = 103; // ratio ~1400x800
      doc.addImage(img, "PNG", 14, 24, iw, ih);
      doc.setFontSize(7); doc.setTextColor(120);
      doc.text("Tracés des trajets · départ (vert) → arrivée (rouge)", 14, 24 + ih + 4);
      tableY = 24 + ih + 9;
    }
  } catch (e) { /* pas de visuel si échec canvas */ }

  autoTable(doc, {
    head: [HEAD], body: ROWS(trips, a), startY: tableY,
    styles: { fontSize: 7, cellPadding: 1.2 }, headStyles: { fillColor: [23, 162, 184] },
    columnStyles: { 4: { cellWidth: 55 }, 5: { cellWidth: 55 } },
  });
  doc.save(`historique_${label || "flotte"}.pdf`);
}

export function exportTripsGPX(trips, label = "") {
  const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<gpx version="1.1" creator="Boomerang Tracking" xmlns="http://www.topografix.com/GPX/1/1">\n';
  trips.forEach((t, i) => {
    xml += `  <trk>\n    <name>${esc(t.vehicle_name || "Trajet")} ${esc(t.plate || "")} #${i + 1}</name>\n    <trkseg>\n`;
    (t.points || []).forEach((p) => {
      xml += `      <trkpt lat="${p.lat}" lon="${p.lon}">`;
      if (p.timestamp) xml += `<time>${p.timestamp}</time>`;
      if (p.speed != null) xml += `<speed>${(p.speed / 3.6).toFixed(2)}</speed>`;
      xml += `</trkpt>\n`;
    });
    xml += `    </trkseg>\n  </trk>\n`;
  });
  xml += "</gpx>\n";
  dl(new Blob([xml], { type: "application/gpx+xml" }), `historique_${label || "flotte"}.gpx`);
}


export function exportDriverReportPDF(trips, label = "") {
  const byDrv = {};
  (trips || []).forEach((t) => {
    const name = t.driver_name || "Non identifié";
    const d = byDrv[name] || (byDrv[name] = { name, km: 0, min: 0, count: 0, harsh_accel: 0, harsh_brake: 0, cornering: 0, overspeed: 0, idle_min: 0 });
    d.km += t.distance_km || 0;
    d.min += t.duration_min || 0;
    d.count += 1;
    (t.events || []).forEach((e) => {
      if (e.type in d && e.type !== "idle") d[e.type] += 1;
      if (e.type === "idle") d.idle_min += e.duration_min || 0;
    });
  });
  const rows = Object.values(byDrv).map((d) => {
    const km100 = Math.max(d.km / 100, 0.1);
    const penalty = (d.harsh_brake / km100) * 0.15 + (d.harsh_accel / km100) * 0.15 + (d.cornering / km100) * 0.1 + (d.overspeed / km100) * 0.4 + d.idle_min * 0.05;
    return { ...d, score: Math.max(0, Math.min(100, Math.round(100 - penalty))) };
  }).sort((a, b) => b.km - a.km);

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  doc.setFontSize(14); doc.setTextColor(23, 162, 184);
  doc.text("Rapport conducteurs — Boomerang Tracking", 14, 14);
  doc.setFontSize(9); doc.setTextColor(80);
  doc.text(`${label} · ${rows.length} conducteur(s)`, 14, 20);
  autoTable(doc, {
    head: [["Conducteur", "Trajets", "Distance (km)", "Conduite", "Éco-score", "Accél.", "Frein.", "Virages", "Excès"]],
    body: rows.map((d) => [d.name, String(d.count), d.km.toFixed(1), fmtDur(Math.round(d.min)), String(d.score), String(d.harsh_accel), String(d.harsh_brake), String(d.cornering), String(d.overspeed)]),
    startY: 25, styles: { fontSize: 8, cellPadding: 1.5 }, headStyles: { fillColor: [23, 162, 184] },
    columnStyles: { 0: { cellWidth: 55 } },
  });
  const slug = String(label || "flotte").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "").toLowerCase() || "flotte";
  doc.save(`rapport_conducteurs_${slug}.pdf`);
}
